/**
 * @fileoverview Match3 Lobby Screen
 * @description Game lobby with mode selection, stats, and leaderboard
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter, useFocusEffect } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import {
  Match3Leaderboard,
  Match3UserStats,
  Match3GameConfig,
  Match3WeekendConfig,
  DEFAULT_MATCH3_GAME_CONFIG,
  DEFAULT_MATCH3_WEEKEND_CONFIG,
  EMPTY_MATCH3_STATS,
} from "@/modules/match3";

type GameMode = "free" | "weekend";

/**
 * Match3 Lobby Screen
 */
export default function Match3LobbyScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeProfile, user, activeWeekend, userTeam } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMode, setSelectedMode] = useState<GameMode>("free");
  const [myStats, setMyStats] = useState<Match3UserStats>(EMPTY_MATCH3_STATS);
  const [gameConfig, setGameConfig] = useState<Match3GameConfig>(DEFAULT_MATCH3_GAME_CONFIG);
  const [weekendConfig, setWeekendConfig] = useState<Match3WeekendConfig>(DEFAULT_MATCH3_WEEKEND_CONFIG);
  const [gameEnabledForWeekend, setGameEnabledForWeekend] = useState(false);
  const [playsToday, setPlaysToday] = useState(0);
  const [leaderboardKey, setLeaderboardKey] = useState(0);

  /**
   * Fetch all data
   */
  const fetchData = useCallback(async () => {
    if (!activeProfile?.id) return;

    try {
      // Fetch game config from game_definitions
      const { data: gameDef } = await supabase
        .from("game_definitions")
        .select("default_config, default_points_config")
        .eq("slug", "match_3")
        .single();

      if (gameDef?.default_config) {
        setGameConfig({
          ...DEFAULT_MATCH3_GAME_CONFIG,
          ...gameDef.default_config,
        });
      }

      // Fetch weekend game config if active weekend exists
      if (activeWeekend) {
        const { data: weekendGame } = await supabase
          .from("weekend_game_config")
          .select("is_enabled, points_config, custom_config")
          .eq("weekend_id", activeWeekend.id)
          .eq("game_slug", "match_3")
          .single();

        setGameEnabledForWeekend(weekendGame?.is_enabled ?? false);

        if (weekendGame?.is_enabled) {
          setWeekendConfig({
            ...DEFAULT_MATCH3_WEEKEND_CONFIG,
            ...(weekendGame.points_config as any),
            ...(weekendGame.custom_config as any),
          });

          // Fetch plays today
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const { count } = await supabase
            .from("game_sessions")
            .select("id", { count: "exact", head: true })
            .eq("created_by", activeProfile.id)
            .eq("game_slug", "match_3")
            .eq("mode", "weekend")
            .gte("created_at", today.toISOString());

          setPlaysToday(count || 0);
        }
      } else {
        setGameEnabledForWeekend(false);
      }

      // Fetch user stats
      const { data: statsData } = await supabase
        .from("user_game_stats")
        .select("stats, personal_best_score, games_played")
        .eq("user_id", activeProfile.id)
        .eq("game_slug", "match_3")
        .eq("mode", selectedMode)
        .single();

      if (statsData?.stats) {
        setMyStats({
          ...EMPTY_MATCH3_STATS,
          ...statsData.stats,
          best_score: statsData.personal_best_score || 0,
          games_played: statsData.games_played || 0,
        });
      } else {
        setMyStats(EMPTY_MATCH3_STATS);
      }

    } catch (error) {
      console.error("Error fetching Match3 data:", error);
    } finally {
      setLoading(false);
    }
  }, [activeProfile?.id, selectedMode, activeWeekend?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Refresh data when screen gains focus (e.g. returning from game)
   */
  useFocusEffect(
    useCallback(() => {
      fetchData();
      // Increment key to force leaderboard refresh
      setLeaderboardKey(prev => prev + 1);
    }, [fetchData])
  );

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  /**
   * Start a new game
   */
  const handleStartGame = async () => {
    if (!activeProfile?.id) return;

    try {
      // Check if can play in weekend mode
      if (selectedMode === "weekend" && !weekendConfig.allow_unlimited_plays) {
        if (playsToday >= weekendConfig.max_plays_per_day) {
          // TODO: Show alert
          return;
        }
      }

      // Create game session
      const { data: session, error } = await supabase
        .from("game_sessions")
        .insert({
          created_by: activeProfile.id,
          game_slug: "match_3",
          mode: selectedMode,
          session_type: "solo",
          status: "active",
          weekend_id: selectedMode === "weekend" ? activeWeekend?.id : null,
          game_state: {
            config: gameConfig,
          },
        })
        .select("id")
        .single();

      if (error) throw error;

      // Navigate to game
      router.push({
        pathname: "/(tabs)/games/match3/[sessionId]",
        params: {
          sessionId: session.id,
          mode: selectedMode,
        },
      });
    } catch (error) {
      console.error("Error starting Match3 game:", error);
    }
  };

  /**
   * Calculate potential points for weekend mode
   */
  const getPotentialPoints = (): string => {
    const min = weekendConfig.points_participation;
    const max = weekendConfig.points_participation + 
                weekendConfig.points_new_highscore + 
                weekendConfig.points_score_5000;
    return `${min} - ${max}`;
  };

  // Determine if weekend mode is available
  const isWeekendModeAvailable = !!activeWeekend && !!userTeam && gameEnabledForWeekend;
  const weekendUnavailableReason = !activeWeekend
    ? "Geen actief weekend"
    : !userTeam
    ? "Je zit nog niet in een team"
    : !gameEnabledForWeekend
    ? "Niet geactiveerd voor dit weekend"
    : "";

  /**
   * Can play check
   */
  const canPlay = (): boolean => {
    if (selectedMode === "free") return true;
    if (!isWeekendModeAvailable) return false;
    if (weekendConfig.allow_unlimited_plays) return true;
    return playsToday < weekendConfig.max_plays_per_day;
  };

  if (loading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "left", "right"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top", "left", "right"]}
    >
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
      >
        {/* Game Description */}
        <View
          className="mx-4 mt-4 p-4 rounded-xl"
          style={{ backgroundColor: colors.surface }}
        >
          <View className="flex-row items-center mb-2">
            <Ionicons name="diamond" size={24} color={colors.primary} />
            <Text
              className="ml-2 text-lg font-bold"
              style={{ color: colors.text }}
            >
              Match 3
            </Text>
          </View>
          <Text style={{ color: colors.textSecondary }}>
            Wissel edelstenen om 3 of meer op een rij te krijgen. 
            Scoor zo hoog mogelijk voordat de tijd op is! 
            Maak combo's voor extra punten en verdien bonus tijd.
          </Text>
        </View>

        {/* Mode Selector */}
        <View className="mx-4 mt-4 flex-row gap-3">
          <TouchableOpacity
            className="flex-1 p-4 rounded-xl"
            style={{
              backgroundColor:
                selectedMode === "free" ? colors.primary : colors.surface,
              borderWidth: selectedMode === "free" ? 0 : 1,
              borderColor: colors.border,
            }}
            onPress={() => setSelectedMode("free")}
            accessibilityRole="button"
            accessibilityLabel="Vrij Spelen"
          >
            <View className="items-center">
              <Ionicons
                name="game-controller"
                size={24}
                color={selectedMode === "free" ? "#FFFFFF" : colors.primary}
              />
              <Text
                className="mt-2 font-semibold"
                style={{
                  color: selectedMode === "free" ? "#FFFFFF" : colors.text,
                }}
              >
                Vrij Spelen
              </Text>
              <Text
                className="text-xs mt-1"
                style={{
                  color:
                    selectedMode === "free"
                      ? "rgba(255,255,255,0.8)"
                      : colors.textSecondary,
                }}
              >
                Ranglijst
              </Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            className="flex-1 p-4 rounded-xl"
            style={{
              backgroundColor:
                selectedMode === "weekend" ? colors.primary : colors.surface,
              borderWidth: selectedMode === "weekend" ? 0 : 1,
              borderColor: colors.border,
              opacity: isWeekendModeAvailable ? 1 : 0.5,
            }}
            onPress={() => isWeekendModeAvailable && setSelectedMode("weekend")}
            disabled={!isWeekendModeAvailable}
            accessibilityRole="button"
            accessibilityLabel="Familie Weekend"
          >
            <View className="items-center">
              <Ionicons
                name="trophy"
                size={24}
                color={selectedMode === "weekend" ? "#FFFFFF" : colors.warning}
              />
              <Text
                className="mt-2 font-semibold"
                style={{
                  color: selectedMode === "weekend" ? "#FFFFFF" : colors.text,
                }}
              >
                Familie Weekend
              </Text>
              <Text
                className="text-xs mt-1"
                style={{
                  color:
                    selectedMode === "weekend"
                      ? "rgba(255,255,255,0.8)"
                      : colors.textSecondary,
                }}
              >
                {isWeekendModeAvailable ? `${getPotentialPoints()} punten` : weekendUnavailableReason || "Niet actief"}
              </Text>
            </View>
          </TouchableOpacity>
        </View>

        {/* Weekend Info */}
        {selectedMode === "weekend" && isWeekendModeAvailable && (
          <View
            className="mx-4 mt-4 p-4 rounded-xl"
            style={{ backgroundColor: colors.warningLight }}
          >
            <View className="flex-row items-center mb-2">
              <Ionicons name="information-circle" size={20} color={colors.warning} />
              <Text className="ml-2 font-semibold" style={{ color: colors.warning }}>
                Weekend Mode
              </Text>
            </View>
            <Text className="text-sm" style={{ color: colors.warning }}>
              • {weekendConfig.points_participation} punten voor deelname{"\n"}
              • {weekendConfig.points_score_500}+ punten bij score ≥ 500{"\n"}
              • {weekendConfig.points_score_1000}+ punten bij score ≥ 1000{"\n"}
              • {weekendConfig.points_score_2000}+ punten bij score ≥ 2000{"\n"}
              • {weekendConfig.points_new_highscore} bonus bij nieuw familie record{"\n"}
              • Max {weekendConfig.max_plays_per_day}x per dag ({weekendConfig.max_plays_per_day - playsToday} over)
            </Text>
            {userTeam && (
              <View className="flex-row items-center mt-2 pt-2 border-t" style={{ borderColor: colors.warning }}>
                <Text className="text-sm" style={{ color: colors.warning }}>
                  Team: {userTeam.name}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* My Stats */}
        <View
          className="mx-4 mt-4 p-4 rounded-xl"
          style={{ backgroundColor: colors.surface }}
        >
          <Text className="font-bold mb-3" style={{ color: colors.text }}>
            Mijn Statistieken
          </Text>
          <View className="flex-row flex-wrap gap-4">
            <View className="items-center">
              <Text className="text-2xl font-bold" style={{ color: colors.primary }}>
                {myStats.best_score.toLocaleString()}
              </Text>
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Beste Score
              </Text>
            </View>
            <View className="items-center">
              <Text className="text-2xl font-bold" style={{ color: colors.text }}>
                {myStats.games_played}
              </Text>
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Gespeeld
              </Text>
            </View>
            <View className="items-center">
              <Text className="text-2xl font-bold" style={{ color: colors.warning }}>
                {myStats.best_combo}x
              </Text>
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Beste Combo
              </Text>
            </View>
            <View className="items-center">
              <Text className="text-2xl font-bold" style={{ color: colors.success }}>
                {myStats.best_time_bonuses}s
              </Text>
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Meeste Bonus
              </Text>
            </View>
          </View>
        </View>

        {/* Time Bonuses Info */}
        <View
          className="mx-4 mt-4 p-4 rounded-xl"
          style={{ backgroundColor: colors.surface }}
        >
          <View className="flex-row items-center mb-3">
            <Ionicons name="time" size={18} color={colors.success} />
            <Text className="ml-2 font-bold" style={{ color: colors.text }}>
              Tijd Bonussen
            </Text>
          </View>
          <Text className="text-sm mb-2" style={{ color: colors.textSecondary }}>
            Verdien extra tijd door score mijlpalen te behalen:
          </Text>
          {gameConfig.time_bonuses.map((bonus, index) => (
            <View key={index} className="flex-row items-center mt-1">
              <Ionicons name="add-circle" size={14} color={colors.success} />
              <Text className="ml-2 text-sm" style={{ color: colors.text }}>
                +{bonus.bonus_seconds}s bij {bonus.score_threshold.toLocaleString()} punten
              </Text>
            </View>
          ))}
        </View>

        {/* Start Button */}
        <View className="mx-4 mt-4">
          <TouchableOpacity
            className="p-4 rounded-xl flex-row items-center justify-center"
            style={{
              backgroundColor: canPlay() ? colors.primary : colors.surfaceSecondary,
            }}
            onPress={handleStartGame}
            disabled={!canPlay()}
            accessibilityRole="button"
            accessibilityLabel="Start spel"
          >
            <Ionicons
              name="play"
              size={24}
              color={canPlay() ? "#FFFFFF" : colors.textTertiary}
            />
            <Text
              className="ml-2 text-lg font-bold"
              style={{ color: canPlay() ? "#FFFFFF" : colors.textTertiary }}
            >
              Start Spel
            </Text>
          </TouchableOpacity>

          {selectedMode === "weekend" && !canPlay() && (
            <Text
              className="text-center mt-2 text-sm"
              style={{ color: colors.error }}
            >
              Je hebt vandaag al {weekendConfig.max_plays_per_day}x gespeeld
            </Text>
          )}
        </View>

        {/* Leaderboard */}
        <View className="mt-4 mb-8">
          <Match3Leaderboard
            key={`${selectedMode}-${leaderboardKey}`}
            mode={selectedMode}
            weekendId={selectedMode === "weekend" ? activeWeekend?.id : undefined}
            maxEntries={10}
            onUserPress={(userId) => router.push(`/scores/user/${userId}`)}
          />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

